/**
 * Electrical Installation Condition Report (EICR) - BS 7671 Model Form Schema
 */

const PURPOSE_OPTIONS = [
  'Periodic inspection and testing (landlord/legal obligation)',
  'Mortgage / sale / letting requirement',
  'Insurance requirement',
  'Change of tenancy',
  'Following a previous unsatisfactory report',
  'New owner / occupier',
  'Other'
];

const SUPPLY_SYSTEMS = ['TN-S', 'TN-C-S', 'TN-C', 'TT', 'IT'];
const OBSERVATION_CLASSES = [
  { value: 'C1', label: 'C1 - Danger Present' },
  { value: 'C2', label: 'C2 - Potentially Dangerous' },
  { value: 'C3', label: 'C3 - Improvement Recommended' },
  { value: 'FI', label: 'FI - Further Investigation Required' }
];

export default {
  sections: [
    {
      id: 'reportDetails',
      title: '1. Report Details',
      fields: [
        { id: 'reportNumber', type: 'text', label: 'Report / Certificate Number', required: true },
        { id: 'reportDate', type: 'date', label: 'Date of Inspection', required: true },
        { id: 'purposeOfReport', type: 'select', label: 'Purpose of Report', options: PURPOSE_OPTIONS, required: true },
        { id: 'inspectionTypeLabel', type: 'select', label: 'Type of Inspection', options: ['Periodic Inspection', 'Condition Report', 'Re-inspection'] }
      ]
    },
    {
      id: 'client',
      title: '2. Client / Customer Details',
      fields: [
        { id: 'clientName', type: 'text', label: 'Client / Customer Name', required: true },
        { id: 'clientAddress', type: 'textarea', label: 'Client Address' }
      ]
    },
    {
      id: 'property',
      title: '3. Premises / Property',
      fields: [
        { id: 'propertyAddress', type: 'textarea', label: 'Property Address', required: true },
        { id: 'propertyType', type: 'select', label: 'Property Type', options: ['House', 'Flat', 'Bungalow', 'Maisonette', 'Commercial', 'Other'] },
        { id: 'numberOfBedrooms', type: 'number', label: 'Number of Bedrooms / Floors' },
        { id: 'estimatedAgeOfInstallation', type: 'text', label: 'Estimated Age of Installation' }
      ]
    },
    {
      id: 'installation',
      title: '4. Installation Details / Extent & Limitations',
      fields: [
        { id: 'installationAddress', type: 'textarea', label: 'Installation Address (if different)' },
        { id: 'extentLimitations', type: 'textarea', label: 'Extent & Limitations of Inspection' },
        { id: 'mainSupply', type: 'select', label: 'Main Supply Type', options: SUPPLY_SYSTEMS },
        { id: 'earthingArrangement', type: 'select', label: 'Earthing Arrangement', options: SUPPLY_SYSTEMS },
        { id: 'externalZe', type: 'number', label: 'External Earth Loop Impedance (Ze)', step: 0.01 }
      ]
    },
    {
      id: 'distributionBoards',
      title: '5. Distribution Board Schedule',
      repeatable: true,
      fields: [
        { id: 'boardName', type: 'text', label: 'Board Reference (e.g. DB1)' },
        { id: 'boardLocation', type: 'text', label: 'Board Location' },
        { id: 'numberOfWays', type: 'text', label: 'Number of Ways' },
        { id: 'mainSwitch', type: 'text', label: 'Main Switch' },
        { id: 'rcdPresent', type: 'boolean', label: 'RCD / RCBO Present', options: ['Yes', 'No'] },
        { id: 'rcdType', type: 'text', label: 'RCD Type' }
      ]
    },
    {
      id: 'circuits',
      title: '6. Schedule of Test Results - Circuit Details',
      repeatable: true,
      fields: [
        { id: 'circuitNumber', type: 'text', label: 'Circuit Number' },
        { id: 'boardName', type: 'text', label: 'Distribution Board' },
        { id: 'circuitDescription', type: 'text', label: 'Circuit Description' },
        { id: 'location', type: 'text', label: 'Location', required: true },
        { id: 'phase', type: 'select', label: 'Phase', options: ['Single Phase', 'Three Phase', 'L1', 'L2', 'L3'] },
        { id: 'protectiveDevice', type: 'text', label: 'Protective Device' },
        { id: 'deviceRating', type: 'text', label: 'Device Rating (A)' },
        { id: 'maxZs', type: 'number', label: 'Max Permitted Zs', step: 0.01 },
        { id: 'measuredZs', type: 'number', label: 'Measured Zs (Ω)', step: 0.01 },
        { id: 'rcdType', type: 'text', label: 'RCD Type' },
        { id: 'rcdTripTime1x', type: 'text', label: 'RCD Trip 1Δn (ms)' },
        { id: 'rcdTripTime5x', type: 'text', label: 'RCD Trip 5Δn (ms)' },
        { id: 'insulationResistance', type: 'text', label: 'Insulation Resistance (MΩ)' },
        { id: 'testResult', type: 'select', label: 'Result', options: ['SATISFACTORY', 'UNSATISFACTORY'], required: true }
      ]
    },
    {
      id: 'observations',
      title: '7. Observations',
      repeatable: true,
      fields: [
        { id: 'observationCode', type: 'select', label: 'Classification', options: OBSERVATION_CLASSES, required: true },
        { id: 'observationDetails', type: 'textarea', label: 'Observation / Defect Details', required: true },
        { id: 'observationLocation', type: 'text', label: 'Location / Item' },
        { id: 'photo', type: 'text', label: 'Photo Attachment (ref)' },
        { id: 'limitations', type: 'select', label: 'Limitation', options: ['None', 'Not tested', 'No access', 'Not inspected'] }
      ]
    },
    {
      id: 'recommendation',
      title: '8. Summary & Recommendations',
      fields: [
        { id: 'summaryCondition', type: 'textarea', label: 'Summary of Installation Condition' },
        { id: 'recommendations', type: 'textarea', label: 'Recommendations for Improvement' }
      ]
    },
    {
      id: 'declaration',
      title: '9. Declaration',
      fields: [
        { id: 'engineerName', type: 'text', label: 'Engineer Name', required: true },
        { id: 'engineerCardNumber', type: 'text', label: 'Engineer Card / License Number', required: true },
        { id: 'companyName', type: 'text', label: 'Company' },
        { id: 'engineerSignature', type: 'signature', label: 'Engineer Signature' },
        { id: 'clientSignature', type: 'signature', label: 'Client Signature' }
      ]
    }
  ]
};